import * as THREE from 'three';
import { chain, forEach, map, reduce } from 'ramda';
import { merge, addOperator as sceneAddOperator, addNode as sceneAddNode, set as sceneSet, get as sceneGet, clone as sceneClone, exportJSON as exportSceneJSON, setVRCamera as setSceneVRCamera } from './sceneGraph';
import { filter as filterAssets, removeFile, exportJSON as exportAssetsJSON } from './assets';
import { getEvalSceneGraph, findParent, findRoot } from './sceneGraph';
import { buildQueryObject, getMatch, findChild, matchChildren } from './utils/selectors';
import records, { lookupOperator } from '../operators/nonImmutable';

/**
 * This module is a high level scene graph manipulation and query library.
 *
 * * Either id, name, or regex's can be used for node names.
 * * All `get` functions return the value, or undefined if no match is found
 * * All `find` functions return an `id` or a Path
 * * All `filter` functions return an array of `id` or Path
 * *
 * @module scene
 * @public
 *
 */

/**
 * Finds first matching node.
 * If propertyName is specified, will look for the first matching operator,
 * and if found, return a path to the operator property.
 * (See {@tutorial sdk-SceneGraph})
 *
 * @public
 * @param {(String|QueryObject)} query The query object to filter on (or a string that gets converted to a query object).
 *
 * @example
 *
 * // Return the node uuid
 * scene.find('Box'); -> 'uuid'
 * // Return the path to the transform operator
 * scene.find('Box','Transform','translation'); -> ['uuid','Transform',0]
 * // Match the first node that starts with Box
 * scene.find('Box*');
 *
 */
export function find(store, query, onlyNode) {
  const queryObj = buildQueryObject(query);
  const sceneGraph = store.get('sceneGraph');

  // Don't need to do a search if we have an id
  if (queryObj.id) {
    let foundNode = sceneGraph.nodes[queryObj.id];
    if (foundNode && queryObj.parent) {
      return foundNode.parent || null;
    }

    if (foundNode && queryObj.child) {
      return findChild(store, foundNode, { type: queryObj.child, shallow: true }, false, true) || null;
    }

    return foundNode ? getMatch(foundNode, queryObj, onlyNode) : null;
  }

  // If we ask to search `all`, then we do a linear search. This lets us
  // find nodes from scenes not in the active scene graph.
  if (queryObj.from && queryObj.from.all) {
    let result;
    for (let id of Object.keys(sceneGraph.nodes)) {
      result = getMatch(sceneGraph.nodes[id], queryObj);
      if (result) return result;
    }

    return null;
  }

  const rootId = queryObj.from ? find(store, queryObj.from, true) :
    store.getIn(['sceneIO', 'id']);
  const rootNode = rootId && sceneGraph.nodes[rootId];

  return rootNode ? findChild(store, rootNode, queryObj, queryObj.includeParent && rootNode, onlyNode) : null;
};

function matchSceneNode(store, queryObj, onlyNode) {
  const node = store.getIn('sceneGraph').nodes[store.getIn(['sceneIO', 'id'])];
  return node && getMatch(node, queryObj, onlyNode) || null;
};

/**
 * This function will iterate through the scene graph, returning an array
 * of all elements that match the parameters provided.
 *
 * The return value of this function will either be an array of ids (if nodequery and
 * plug are provied), or an array of paths (if property is provided).
 *
 * (See {@tutorial sdk-SceneGraph})
 *
 * @public
 * @param {(String|QueryObject)} query The query object to filter on (or a string that gets converted to a query object).
 * @returns {Array}
 */
export function filter(store, query) {
  const queryObj = buildQueryObject(query);
  const fromId = queryObj.from ? find(store, queryObj.from) : store.getIn(['sceneIO', 'id']);

  // No matching from query, return empty search
  if (queryObj.from && !fromId) return [];

  const fromNode = store.get('sceneGraph').nodes[fromId];

  if (!fromNode) {
    throw new Error('Unknown node to filter from');
  }

  return matchChildren(store, fromNode, queryObj, queryObj.includeParent && fromNode);
};

/**
 * Returns the value at the matching property.
 *
 * (See {@tutorial sdk-SceneGraph})
 *
 * @param {(String|QueryObject|Path)} query The query object to filter on (or a string that gets converted to a query object).
 * @returns {any}
 * @public
 */
export function get(store, query) {
  const path = Array.isArray(query) ? query : find(store, query);
  if (!path || !Array.isArray(path)) return undefined;
  if (path.length >= 2 && path[1] === 'eval') {
    const evalNode = getEvalSceneGraph(store).evaluatedNodes[path[0]];
    const evalPlug = evalNode && evalNode[path[2]];
    return evalPlug && path[3] ? evalPlug[path[3]] : evalPlug;
  }

  return sceneGet(store, path);
};

/**
 * Returns an array of values for the matching properties
 *
 * (See {@tutorial sdk-SceneGraph})
 *
 * @param {(String|QueryObject|Path)} query The query object to filter on (or a found path)
 * @returns {any[]}
 * @public
 */
export function getAll(store, query) {
  const paths = Array.isArray(query) ? query : filter(store, query);
  return paths ? reduce((obj, path) => {
    obj[path[0]] = sceneGet(store, path);
    return obj;
  }, {}, paths) : undefined;
};

/**
 * Returns the evaluated plug (and/or property).
 * @param {String} nodeId id of the node
 * @param {String} plug
 * @param {?String} key
 *
 * @example
 *
 * // Return the calculated transform
 * ctx.getEval('Box','Transfrom','translation');
 * // Return the orbitTarget for the scene camera
 * ctx.getEval(true, 'Player', 'orbitTarget');
 */
export function getEval(store, nodeId, plug, key) {
  return get(store, key ? [nodeId, plug, key] : [nodeId, plug]);
};

/**
 * Get the world transform for a node.
 *
 * @param {String} id
 * @public
 * @returns {?THREE.Matrix4}
 */
export function getWorldTransform(store, id) {
  const transform = get(store, { id, evalPlug: 'Transform' });
  if (!transform) return null;

  const localTransform = transform.transform;
  const node = store.get('sceneGraph').nodes[id];
  const parent = node && node.parent;

  const parentTransform = parent && getWorldTransform(store, parent);
  return parentTransform ? new THREE.Matrix4().copy(parentTransform).multiply(localTransform)
    : new THREE.Matrix4().copy(localTransform);
}


/**
 * Get the transform for a node relative to a specified ancestor node. If rootId is not in id's
 *  ancestry, the result is simply the world transform.
 *
 * @param {String} id
 * @param {String} rootId - the node for which we want to calculated id's relative transform from
 * @public
 * @returns {?THREE.Matrix4}
 */
export function getRelativeTransform(store, id, rootId) {
  const transform = get(store, { id, evalPlug: 'Transform' });
  if (!transform || id === rootId) return null;

  const localTransform = transform.transform;
  const node = store.get('sceneGraph').nodes[id];
  const parent = node && node.parent;
  const parentTransform = parent && getRelativeTransform(store, parent, rootId);
  return parentTransform ? new THREE.Matrix4().copy(parentTransform).multiply(localTransform)
    : new THREE.Matrix4().copy(localTransform);
}


/**
 * Get the local transform for a node.
 *
 * @returns {?THREE.Matrix4}
 */
export function getLocalTransform(transform) {
  const { translation, rotation, scale, rotateOrder, preRotation,
    rotatePivotOffset, localRotatePivot, scalePivotOffset,
    localScalePivot, shear, } = transform;
  if (scale.x === 0) scale.x = 0.0000001;
  if (scale.y === 0) scale.y = 0.0000001;
  if (scale.z === 0) scale.z = 0.0000001;
  const zero = new THREE.Vector3(0, 0, 0);

  let matrixStack = [];

  const translationM = new THREE.Matrix4()
    .makeTranslation(translation.x, translation.y, translation.z);
  matrixStack.push(translationM);

  const rotatePivotOffestM = new THREE.Matrix4();
  if (!rotatePivotOffset.equals(zero)) {
    rotatePivotOffestM.makeTranslation(rotatePivotOffset.x, rotatePivotOffset.y, rotatePivotOffset.z);
    matrixStack.push(rotatePivotOffestM);
  }

  const localRotatePivotM = new THREE.Matrix4();
  const invLocalRotatePivotM = new THREE.Matrix4();
  let localRotatePivotFlag = false;
  if (!localRotatePivot.equals(zero)) {
    localRotatePivotM.makeTranslation(localRotatePivot.x, localRotatePivot.y, localRotatePivot.z);
    matrixStack.push(localRotatePivotM);
    invLocalRotatePivotM.getInverse(localRotatePivotM, true);
    localRotatePivotFlag = true;
  }

  const preRotationM = new THREE.Matrix4();
  if (!preRotation.equals(zero)) {
    const localRotationRadians = new THREE.Vector3().copy(preRotation).multiplyScalar(Math.PI / 180);
    preRotationM.makeRotationFromEuler(new THREE.Euler(
      localRotationRadians.x, localRotationRadians.y, localRotationRadians.z, rotateOrder
    ));
    matrixStack.push(preRotationM);
  }

  const rotationRadians = new THREE.Vector3().copy(rotation).multiplyScalar(Math.PI / 180);
  const rotationM = new THREE.Matrix4()
    .makeRotationFromEuler(new THREE.Euler(rotationRadians.x, rotationRadians.y, rotationRadians.z, rotateOrder));
  matrixStack.push(rotationM);

  if (localRotatePivotFlag) matrixStack.push(invLocalRotatePivotM);

  const scalePivotOffsetM = new THREE.Matrix4();
  if (!scalePivotOffset.equals(zero)) {
    scalePivotOffsetM.makeTranslation(scalePivotOffset.x, scalePivotOffset.y, scalePivotOffset.z);
    matrixStack.push(scalePivotOffsetM);
  }

  const localScalePivotM = new THREE.Matrix4();
  const invLocalScalePivotM = new THREE.Matrix4();
  let localScalePivotFlag = false;
  if (!localScalePivot.equals(zero)) {
    localScalePivotM.makeTranslation(localScalePivot.x, localScalePivot.y, localScalePivot.z);
    matrixStack.push(localScalePivotM);
    invLocalScalePivotM.getInverse(localScalePivotM, true);
    localScalePivotFlag = true;
  }

  const sX = shear.x, sY = shear.y, sZ = shear.z;
  const shearM = new THREE.Matrix4().set(
    1, sX, sY, 0,
    0, 1, sZ, 0,
    0, 0, 1, 0,
    0, 0, 0, 1
  ); //.makeShear(new THREE.Vector3(0, 0, 0));
  matrixStack.push(shearM);

  const scaleM = new THREE.Matrix4().makeScale(scale.x, scale.y, scale.z);
  matrixStack.push(scaleM);

  if (localScalePivotFlag) matrixStack.push(invLocalScalePivotM);

  const matrixAccumulation = new THREE.Matrix4();
  for (let i = 0; i < matrixStack.length; i++) {
    matrixAccumulation.multiply(matrixStack[i]);
  }

  return matrixAccumulation;
}

/**
 * Sets the property at path to value.
 *
 * (See {@tutorial sdk-SceneGraph})
 *
 * @param {(Path|QueryObject|String)} query The query object to filter on (or a string that gets converted to a query object).
 * @param {any} value The value to set, the type must match the property being set.
 * @public
 */
export function set(query, value) {
  return (store) => {
    const path = Array.isArray(query) ? query : find(store, query);
    if (!path) {
      console.warn('No matching query', query);
      return Promise.resolve();
    }

    if (path.length === 3) {
      if (typeof value !== 'object') throw new Error('If no property is set, expect an object to merge into the operator');
      return store.dispatch(merge(path, value));
    } else if (path.length === 4) {
      return store.dispatch(sceneSet(path, value));
    } else if (path.length === 2) {
      if (path[1] !== 'name') throw new Error('Can only set node name');
      return store.dispatch(sceneSet(path, value));
    }
  };
};

/**
 * Given an array of paths, will set the `value` on each path.
 * This can be combined with `find` to set a number of properties at once.
 *
 * (See {@tutorial sdk-SceneGraph})
 *
 * @public
 * @param {(Path[]|QueryObject|String)} query An array of path arrays, or a QueryObject
 * @param {any} value The value to set the properties to.
 *
 * @example
 *
 * // Move all boxes to the origin
 * const paths = ctx.find('Box*', 'Transform', 'translation');
 * setAll(paths, {x: 0, y: 0, z: 0});
 *
 */
export function setAll(query, value) {
  //if (!Array.isArray(paths)) return Promise.reject('paths must be an array');
  return (store) => {
    const paths = Array.isArray(query) ? query : filter(store, query);
    //console.log('ctx.setAll', paths, value);
    return Promise.all(map((path) => store.dispatch(set(path, value)), paths));
  };
};

/*
 * Returns an array of nodes with warnings in the scene graph.
 * @private
 */
export function getWarnings(store) {
  const sceneGraph = store.get('sceneGraph');
  const nodeIds = Object.keys(sceneGraph.nodes);
  let node;
  const warningNodes = [];
  for (let i = 0; i < nodeIds.length; i++) {
    node = sceneGraph.nodes[nodeIds[i]];
    if (node && node.warnings && node.warnings.length) warningNodes.push(node);
  }

  return warningNodes;
}

export function setVRCamera(query) {
  return (store) => {
    query.type = 'Camera';
    const newVRCamera = find(store, query);
    store.dispatch(setSceneVRCamera(newVRCamera));
  };
}

/**
 * Given a query, clone these nodes. Without options, these nodes will be cloned
 * in place
 *
 * @public
 * @param {(QueryObject|String|Path[])} query A list of nodes (from filter), or query that will find a list of nodes to clone
 * @param {Object} [options]
 * @param {String} [options.to] The scene being cloned to (default is the active scene)
 * @param {String} [options.withNull] Add a null to clone the nodes to
 * @param {Object} [options.includeDependencies] Clone dependencies (default true)
 *
 * @example
 *
 * // Clone all nodes from Objects on import scene into active scene
 * scene.clone({from: {id: importId, child: 'Objects'}});
 *
 * // Clone children of a null to a new null
 * scene.clone({from: {id: importId, child: 'Objects'}}, {withNull: 'New Parent Null'});
 *
 * // Clone children, without also cloning dependencies.
 *
 *
 * // Return the path to the transform operator
 * scene.find('Box','Transform','translation'); -> ['uuid','Transform',0]
 * // Match the first node that starts with Box
 * scene.find('Box*');
 *
 */
export function clone(query, options = {}) {
  if (options.cloneDependencies === undefined) options.cloneDependencies = true;

  return (store) => {
    const paths = Array.isArray(query) ? query : filter(store, query);
    if (!paths.length) {
      console.warn('No match for clone: ', query);
      return Promise.resolve(true);
    }

    const fromSceneId = findRoot(store, paths[0]);
    const toSceneId = options.to || store.getIn(['sceneIO', 'id']);
    const fromObjectsId = find(store, { id: fromSceneId, child: 'Objects' });
    const toObjectsId = find(store, { id: toSceneId, child: 'Objects' });

    const nodeMap = {
      [fromObjectsId]: toObjectsId,
      [find(store, { id: fromSceneId, child: 'MaterialLibrary' })]: find(store, { id: toSceneId, child: 'MaterialLibrary' }),
    };

    const nullNode = options.withNull ? store.dispatch(sceneAddNode({
      name: options.withNull,
      parent: toObjectsId,
      type: 'Null',
      plugs: {
        Null: [['Null', {}]],
        Transform: [['Transform', {}]],
        Properties: [['Default', {}]],
      },
    })) : Promise.resolve(null);

    return nullNode.then((nullNodeId) => {
      if (nullNodeId) nodeMap[fromObjectsId] = nullNodeId;
      return store.dispatch(sceneClone(paths, nodeMap, { cloneDependencies: options.cloneDependencies })).then((cloneMap) => {
        if (nullNodeId) cloneMap[options.withNull] = nullNodeId;
        return cloneMap;
      });
    });
  };
}

export function pruneAssets() {
  return (store) => {
    const sceneGraph = store.get('sceneGraph');
    const usedAssets = {};
    const nodes = filter(store, { includeParent: true });

    function storeFileRefs(id) {
      const plugs = sceneGraph.nodes[id] && sceneGraph.nodes[id].plugs;
      for (let plugName of Object.keys(plugs || {})) {
        plugs[plugName].forEach((op, opIdx) => {
          const operator = lookupOperator(plugName, op.type, []);
          for (let refKey of operator.fileReferenceKeys) {
            if (op[refKey]) usedAssets[op[refKey]] = true;
          }
        });
      }
    };

    nodes.forEach(storeFileRefs);

    return Promise.all(filterAssets(store).map(function (asset) {
      return !usedAssets[asset.get('id')] &&
        store.dispatch(removeFile(asset.get('id')));
    }));
  };
}

export function getNodeBoundingBox(store, id) {
  const translator = store.getTranslator();
  if(translator) return translator.getNodeBoundingBox(id);
  return new THREE.Box3();
};

export function getCurrentPass(store, optionalPassName) {
  if (optionalPassName) {
    return get(store, { name: optionalPassName, evalPlug: 'Pass' });
  }
  const currPassID = get(store, { name: 'Passes', plug: 'GeneralRenderer', property: 'curPass' });
  const currPass = get(store, { id: currPassID, evalPlug: 'Pass' });
  return currPass || get(store, { type: 'Pass', evalPlug: 'Pass' });
};

export function exportJSON(store, sceneId) {
  const _sceneId = sceneId || store.getIn(['sceneIO', 'id']);

  const nodes = filter(store, { from: { id: _sceneId }, includeParent: true });
  return {
    sceneGraph: exportSceneJSON(store, nodes),
    assets: exportAssetsJSON(store),
  };
};

const nodeTypes = {
  //Scene: {
  //  'Environment': 'Environment',
  //  'Timeline': 'Timeline',
  //  'Player': 'Player',
  //},
  BinMesh: {
    'BinMesh': 'ProxyMesh',
    'Transform': 'Transform',
    'Material': 'Reference',
    'Properties': 'BinMeshProperties',
  },
  PolyMesh: {
    'PolyMesh': 'Box',
    'Transform': 'Transform',
    'Material': 'Reference',
    'Properties': 'PolyMeshProperties',
  },
  Light: {
    'Light': 'PointLight',
    'Transform': 'Transform',
    'Properties': 'Default',
  },
  Camera: {
    'Camera': 'Camera',
    'Transform': 'Transform',
    'Properties': 'Default',
  },
  Null: {
    'Null': 'Null',
    'Transform': 'Transform',
    'Properties': 'Default',
  },
  Model: {
    'Null': 'Null',
    'Transform': 'Transform',
    'Mixer': 'Mixer',
    'Properties': 'ModelProperties',
  },
  Material: {
    'Material': 'Physical',
    'Properties': 'MaterialProperties',
  },
  Image: {
    'Image': 'Image',
    'Properties': 'ImageProperties',
  },
  Pass: {
    'Pass': 'Pass'
  },
  Renderer: {
    'Renderer': 'WebGLRenderer',
  },
  Bone: {
    'Bone': 'Bone',
    'Transform': 'BoneTransform',
    'Properties': 'Default',
  },
  Annotation: {
    'Annotation': 'Annotation',
    'Transform': 'Transform',
    'Properties': 'Default',
  },
  // Objects: [],
  // MaterialLibrary: [],
  // Renderers: [],
  // Passes: [],
};

/**
 * Add a node to the scene.
 *
 * @public
 * @param {Node} node The new node being added
 * @param {String} node.name The name of the new node
 * @param {String} node.type The type of the new node
 * @param {String} node.parent the id of the parent node for the new node
 * @param {Object} [node.plugs] The plugs for the new node
 * @returns {Promise}
 *
 */
export function addNode(node) {
  const { name, type, parent, plugs } = node;
  if (!parent) throw new Error('addNode requires parent to be set');
  if (!type) throw new Error('addNode requires type to be set');
  if (!name) throw new Error('addNode requires name to be set');
  if (!nodeTypes[type]) throw new Error(`invalid type: ${type} for addNode`);
  const toPlugs = plugs || {};
  for (let plugName of Object.keys(nodeTypes[type])) {
    if (!toPlugs[plugName]) toPlugs[plugName] = [[nodeTypes[type][plugName], {}]];
    if (!toPlugs[plugName][0][0]) throw new Error('Required operator for ' + plugName);
  }
  return sceneAddNode({ name, type, plugs: toPlugs, parent });
};

/**
 * Add an operator to an existing node in the scene.
 *
 * @public
 * @param {String} id The id of the node receiving the operator
 * @param {String} plugType The plug that is receiving the operator
 * @param {string} operatorType The type of operator being added
 * @param {Object} [operator] The details of the operator
 * @param {String} [name] the name of the operator
 * @returns {Promise}
 *
 */
export function addOperator(id, plugType, operatorType, operator={}, name) {
  if (!id) throw new Error('addOperator requires the id for the element receiving the operator');
  if (!plugType) throw new Error('addOperator requires the plug to be set');
  if (!operatorType) throw new Error('addOperator requires the operator type to be set');

  const plug = records[plugType];
  if (!plug) throw new Error(`Unknown plug: ${plugType}`);
  const op = plug[operatorType];
  if (!op) throw new Error(`Unknown operator: ${operatorType}`);

  return sceneAddOperator(id, plugType, [operatorType, operator, name]);
}

const publicApi = {
  actions: { set, setAll, clone, pruneAssets, setVRCamera, addOperator, addNode },
  selectors: { get, getAll, getWorldTransform, find, filter, getWarnings,
    exportJSON, getNodeBoundingBox, getCurrentPass },
};

export default publicApi;



// WEBPACK FOOTER //
// ./src/modules/scene.js